/**
 * Copyright (c) 2024 Securisea, Inc.
 * All rights reserved.
 *
 * This is unpublished, propriety, internal
 * source code of Securisea, Inc.
 * No license is provided to any third parties.
 * If found outside of a Securisea, Inc. environment
 * report to bounty@securisea.com for a possible cash reward
 * and promptly delete all files.
 */
import React from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import {
  Home as HomeIcon,
  Logout as LogoutIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const Error = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate("/");
  };

  const handleLogout = () => {
    window.location.href = "/logout";
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper 
        elevation={1} 
        sx={{ 
          p: 4, 
          textAlign: "center",
          borderRadius: 1,
          background: "white"
        }}
      >
        <Box sx={{ mb: 4 }}>
          <WarningIcon 
            sx={{ 
              fontSize: 60, 
              color: "text.secondary",
              mb: 2
            }} 
          />
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: "normal",
              color: "text.primary"
            }}
          >
            Something went wrong
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            We encountered an unexpected error. Please try one of the options below.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={2} sx={{ mb: 4 }}>
          <Button
            variant="contained"
            size="medium"
            startIcon={<HomeIcon />}
            onClick={handleGoHome}
            sx={{ 
              py: 1,
              px: 3
            }}
          >
            Go to Home
          </Button>

          <Button
            variant="outlined"
            size="medium"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ 
              py: 1,
              px: 3
            }}
          >
            Refresh Page
          </Button>

          <Button
            variant="outlined"
            color="inherit"
            size="medium"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ 
              py: 1,
              px: 3
            }}
          >
            Logout
          </Button>
        </Stack>

        <Alert 
          severity="info" 
          variant="outlined"
          sx={{ 
            textAlign: "left",
            "& .MuiAlert-message": {
              fontSize: "0.9rem"
            }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "normal", mb: 1 }}>
            Still having issues?
          </Typography>
          <Typography variant="body2">
            If you keep being redirected to this error page, please try logging out and logging back in. 
            This will refresh your session and may resolve the issue.
          </Typography>
        </Alert>

        <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            Error Code: ERR_UNKNOWN | {new Date().toLocaleString()}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Error;
